import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import { MAX_LEN, OrderStatus } from "@app/constants";
import { ImageItem, Order, Package } from "@app/types";
import { getOrderEndTime } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";
import Pagination from "@app/components/pagination";
import {
  IcClock,
  IcArrowLeftRight,
  IcEllipsis,
  IcGlobe,
  IcLink,
  IcRotateCw,
  IcEye,
  IcEyeOff,
  IcGamepad,
  IcKeyRound,
  IcMonitor,
  IcPower,
  IcRefresh,
  IcSearch,
  IcTrash,
} from "@app/components/icons";
import {
  CopyButton,
  LoadingBlock,
  PcGlyph,
  Spinner,
  StatusPill,
  btn,
  btnGhost,
  btnPlay,
  btnPrimary,
  btnSm,
  btnSoft,
  iconBtn,
  miniBtn,
} from "@app/components/ui";

interface RentedTableProps {
  /** Chưa có dữ liệu lần đầu */
  loading?: boolean;
  orders: Order[];
  /** Máy vừa tạo, đang boot (chưa có order): hiện trạng thái Đang khởi tạo */
  booting: ImageItem[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Lọc theo status của order (undefined = tất cả), gọi lại getOrders ở trang cha */
  orderStatus?: OrderStatus;
  orderCount: { busy: number; finished: number };
  onOrderStatusChange: (status?: OrderStatus) => void;
  /** Đơn đã kết thúc: điền lại cấu hình cũ vào "Thông tin tạo máy" */
  onRecreate: (order: Order) => void;
  /** RDP: máy vật lý hay Cloud PC gọi API khác nhau nên truyền cả máy */
  onConnect: (image: ImageItem) => void;
  onPlay: (image: ImageItem) => void;
  onExtend: (order: Order) => void;
  onDelete: (imageId: string) => void;
  onRouter: (image: ImageItem) => void;
  onIpXoay: (image: ImageItem) => void;
  onTunnel: (image: ImageItem) => void;
  onReset: (image: ImageItem) => void;
  /** Bật máy: chỉ dòng Cloud PC đã tạo xong (lỡ shutdown trong Windows / tạo xong máy chưa bật) */
  onStart?: (image: ImageItem) => void;
  /** Đổi mật khẩu (dòng Cloud PC không có mục này) */
  onReConnect?: (imageId: string) => void;
  /** Màu danh mục theo package của máy (khung và đèn vỏ case) */
  colorOf?: (pkg?: Package) => string;
  /** Thao tác đang chạy theo key "<thao tác>:<id máy>" (useBusy): nút đó xoay, không che cả trang */
  busy?: Record<string, boolean>;
  /** _id order vừa đổi mật khẩu: hiện MK mới và tô xanh */
  pwNew?: string;
  /** Giờ hiện tại (ms): đơn đã quá giờ kết thúc thì hiện "Đã kết thúc" ngay, không đợi server tắt máy */
  now?: number;
  /** id của khung (cuộn tới sau khi tạo máy); tab Cloud PC dùng id riêng vì 2 tab cùng được mount */
  anchorId?: string;
}

/** "17/10 · 02:00 → 07:00" nếu cùng ngày, "15/10 17:00 → 16/10 17:00" nếu khác ngày */
const rentalRange = (order: Order) => {
  const start = dayjs(order.time_boot);
  const end = getOrderEndTime(order);

  return start.isSame(end, "day")
    ? `${start.format("DD/MM")} · ${start.format("HH:mm")} → ${end.format("HH:mm")}`
    : `${start.format("DD/MM HH:mm")} → ${end.format("DD/MM HH:mm")}`;
};

/** Bảng "Máy đang thuê": mỗi dòng là 1 order (getOrders), thao tác máy dùng order.image. */
function RentedTable({
  orders,
  booting,
  page,
  totalPages,
  onPageChange,
  orderStatus,
  orderCount,
  onOrderStatusChange,
  loading,
  colorOf,
  pwNew,
  now,
  busy = {},
  anchorId = "rented",
  ...on
}: RentedTableProps) {
  const t = useT();
  const isBusy = (action: string, imageId?: string) => !!imageId && !!busy[`${action}:${imageId}`];
  // các thao tác nằm trong menu "…": đang chạy thì nút "…" của dòng đó xoay
  const MENU_ACTIONS = ["wan", "ip", "tunnel", "reset", "pw"];
  // Menu "…" vẽ dạng fixed bên ngoài bảng để không bị khung cuộn ngang cắt mất
  const [menu, setMenu] = useState<{ order: Order; top?: number; bottom?: number; right: number } | null>(null);
  const [q, setQ] = useState("");
  // Mật khẩu đang được hiện (theo _id order)
  const [revealed, setRevealed] = useState<string[]>([]);

  const menuRef = useRef<HTMLDivElement>(null);

  // Vừa đổi mật khẩu: hiện luôn mật khẩu mới như bản thiết kế
  useEffect(() => {
    if (pwNew) setRevealed((pre) => (pre.includes(pwNew) ? pre : [...pre, pwNew]));
  }, [pwNew]);

  useOnClickOutside(menuRef, () => setMenu(null));

  // Vị trí fixed sẽ lệch khi cuộn hoặc đổi kích thước cửa sổ nên đóng menu
  useEffect(() => {
    if (!menu) return;

    const close = () => setMenu(null);

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu]);

  const toggleMenu = (order: Order, button: HTMLElement) => {
    if (menu?.order._id === order._id) {
      setMenu(null);

      return;
    }

    const rect = button.getBoundingClientRect();
    const right = window.innerWidth - rect.right;
    const menuHeight = 230; // 5 mục

    // Gần đáy màn hình thì mở menu lên phía trên nút
    setMenu(
      rect.bottom + menuHeight > window.innerHeight
        ? { order, bottom: window.innerHeight - rect.top + 6, right }
        : { order, top: rect.bottom + 6, right }
    );
  };

  const toggleReveal = (id: string) =>
    setRevealed((pre) => (pre.includes(id) ? pre.filter((item) => item !== id) : [...pre, id]));

  // Tìm kiếm trên trang hiện tại: tên máy, ID máy, IP remote, tài khoản
  const keyword = q.trim().toLowerCase();
  const rows = orders.filter(
    (order) =>
      !keyword ||
      [order.image._id, order.image.name, order.image.package?.name, order.remote_ext_ip, order.user_remote]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
  );

  // Máy đang khởi tạo chỉ hiện ở "Tất cả" và "Đang chạy"
  const bootingRows =
    orderStatus === OrderStatus.FINISHED
      ? []
      : booting.filter(
          (image) =>
            !keyword ||
            [image._id, image.name, image.package?.name].join(" ").toLowerCase().includes(keyword)
        );

  const unitName = (unit?: string) => (unit === "day" ? "ngày" : unit === "month" ? "tháng" : "giờ");

  const label = "w-7 font-sans text-[11px] text-faint";
  const th =
    "whitespace-nowrap bg-surface-2 px-2 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[.05em] text-faint first:pl-5 last:pr-5";
  const td = "border-t border-line px-2 py-3 align-middle first:pl-5 last:pr-5";

  return (
    <section id={anchorId} className="rented overflow-hidden rounded-[14px] border border-line bg-surface">
      <div className="rented-head border-b border-line px-5 py-4 max-[760px]:px-3.5">
        <h2 className="flex items-center gap-2 text-[17px] font-bold">
          {t("Máy đang thuê")}
          <span className="rounded-full bg-brand-soft px-[9px] py-px text-xs font-semibold text-brand">
            {orderCount.busy + orderCount.finished + booting.length}
          </span>
        </h2>
        <p className="m-0 mt-0.5 text-[13px] text-muted">
          {t("Kết nối, gia hạn, đổi đường truyền, IP xoay. Máy đã kết thúc có thể tạo lại với cấu hình cũ.")}
        </p>
      </div>

      <div className="rented-bar flex flex-wrap items-center gap-2.5 border-b border-line px-5 py-3 max-[760px]:px-3.5">
        <label className="flex h-9 flex-[1_1_260px] items-center gap-2 rounded-[10px] border border-line bg-surface px-3 text-muted focus-within:border-brand">
          <IcSearch className="h-[18px] w-[18px] flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Tìm theo tên máy, ID, IP…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
          />
        </label>
        <div className="rtabs inline-flex gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
          {(
            [
              [undefined, "Tất cả", orderCount.busy + orderCount.finished + booting.length],
              [OrderStatus.BUSY, "Đang chạy", orderCount.busy + booting.length],
              [OrderStatus.FINISHED, "Đã kết thúc", orderCount.finished],
            ] as [OrderStatus | undefined, string, number][]
          ).map(([key, text, count]) => (
            <button
              key={text}
              type="button"
              onClick={() => onOrderStatusChange(key)}
              className={clsx(
                "inline-flex items-baseline gap-1.5 rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                orderStatus === key ? "bg-surface text-fg shadow-card" : "text-muted"
              )}
            >
              {t(text)} <em className="text-[11.5px] not-italic text-faint">{count}</em>
            </button>
          ))}
        </div>
      </div>

      {loading && !orders.length && !bootingRows.length ? (
        <LoadingBlock />
      ) : !rows.length && !bootingRows.length ? (
        <p className="m-0 px-4 py-12 text-center text-muted">
          {orders.length || booting.length ? t("Không tìm thấy máy nào") : t("Bạn chưa thuê máy nào")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] border-collapse text-[13px]">
            <thead>
              <tr>
                <th className={th}>{t("Máy thuê / ID máy")}</th>
                <th className={th}>{t("Tên máy")}</th>
                <th className={th}>{t("Thời gian thuê")}</th>
                <th className={th}>{t("Đăng nhập / IP remote")}</th>
                <th className={th}>{t("Trạng thái")}</th>
                <th className={clsx(th, "!text-right")}>{t("Thao tác")}</th>
              </tr>
            </thead>
            <tbody>
              {bootingRows.map((image) => (
                <tr key={`booting-${image._id}`}>
                  <td className={clsx(td, "c-m")}>
                    <div className="flex items-center gap-2.5">
                      <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[9px] bg-surface-2">
                        <PcGlyph height={30} color={colorOf?.(image.package)} />
                      </span>
                      <div className="min-w-0">
                        <b className="block whitespace-nowrap font-semibold text-fg" title={image.package?.name}>
                          {image.package?.name}
                        </b>
                        <div className="flex items-center gap-0.5 whitespace-nowrap text-[11.5px] text-muted">
                          {t("ID máy")}:{" "}
                          <span className="font-mono" title={image._id}>
                            {image._id.slice(0, 6)}…
                          </span>
                          <CopyButton value={image._id} label="Sao chép ID máy" />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={clsx(td, "c-alias")}>
                    <div className="whitespace-nowrap font-medium">{image.name}</div>
                    {image.os && <div className="text-xs text-muted">{image.os}</div>}
                  </td>
                  <td className={clsx(td, "c-tm whitespace-nowrap tabular-nums")}>
                    {!!image.rental_period && (
                      <span className="mb-0.5 inline-block rounded-md bg-surface-2 px-[7px] py-px text-xs font-semibold text-fg">
                        {image.rental_period} {t(unitName(image.unit))}
                      </span>
                    )}
                    <div className="text-[12.5px] text-muted">{t("Bắt đầu tính khi máy tạo xong")}</div>
                  </td>
                  <td className={clsx(td, "c-cred")}>
                    <div className="revoked flex max-w-[200px] items-center gap-2 text-xs leading-snug text-faint">
                      <IcKeyRound className="h-[15px] w-[15px] flex-none" />
                      {t("Thông tin đăng nhập sẽ hiện khi máy tạo xong")}
                    </div>
                  </td>
                  <td className={clsx(td, "c-st")}>
                    <StatusPill tone="warn">{t("Đang khởi tạo")}</StatusPill>
                  </td>
                  <td className={clsx(td, "c-act")}>
                    <div className="acts is-run">
                      <button type="button" className={clsx(btn, btnPrimary, btnSm, "act-btn")} disabled>
                        <IcMonitor className="h-4 w-4" />
                        RDP
                      </button>
                      <button type="button" className={clsx(btn, btnPlay, btnSm, "act-btn btn-play")} disabled title="Play Game">
                        <IcGamepad className="h-4 w-4" />
                        <span className="lbl">Play Game</span>
                      </button>
                      <button type="button" className={clsx(btn, btnGhost, btnSm, "act-btn btn-ext")} disabled title={t("Gia hạn")}>
                        <IcClock className="h-4 w-4" />
                        <span className="lbl">{t("Gia hạn")}</span>
                      </button>
                      <button
                        type="button"
                        disabled
                        className={clsx(iconBtn, "act-icon del !h-8 !w-8 !rounded-lg text-faint")}
                        title={t("Máy đang tạo, không thể xoá")}
                      >
                        <IcTrash className="h-4 w-4" />
                      </button>
                      <button type="button" disabled className={clsx(iconBtn, "act-icon !h-8 !w-8 !rounded-lg")}>
                        <IcEllipsis className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.map((order) => {
                const image = order.image;
                // Trạng thái theo status của order; đang thuê thì xem image đã cài xong chưa
                // Hết giờ theo đồng hồ nhưng server chưa chạy cron tắt máy (tối đa 5 phút) cũng coi là đã kết thúc
                const timeUp = order.status === OrderStatus.BUSY && !!now && getOrderEndTime(order).valueOf() <= now;
                const ended = order.status === OrderStatus.FINISHED || timeUp;
                const running = !ended && image.is_installed === 1;
                const booting = !ended && !running;
                // Chỉ xoá được máy khi đơn đã kết thúc và máy không còn chạy / đang boot
                const canDelete = ended && image.is_booting === 0 && image.is_installed === 0;
                const shown = revealed.includes(order._id);

                return (
                  <tr
                    key={order._id}
                    title={ended ? t("Bấm để tạo lại máy với cấu hình này") : undefined}
                    onClick={() => ended && on.onRecreate(order)}
                    className={clsx(ended && "cursor-pointer text-muted hover:bg-surface-2")}
                  >
                    <td className={clsx(td, "c-m")}>
                      <div className="flex items-center gap-2.5">
                        <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[9px] bg-surface-2">
                          <PcGlyph height={30} muted={ended} color={colorOf?.(image.package)} />
                        </span>
                        <div className="min-w-0">
                          <b className={clsx("block whitespace-nowrap font-semibold", !ended && "text-fg")} title={image.package?.name}>
                            {image.package?.name}
                          </b>
                          <div className="flex items-center gap-0.5 whitespace-nowrap text-[11.5px] text-muted">
                            {t("ID máy")}:{" "}
                            <span className="font-mono" title={image._id}>
                              {image._id.slice(0, 6)}…
                            </span>
                            <CopyButton value={image._id} label="Sao chép ID máy" />
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className={clsx(td, "c-alias")}>
                      <div className="whitespace-nowrap font-medium">{image.name}</div>
                      {image.os && <div className="text-xs text-muted">{image.os}</div>}
                    </td>

                    <td className={clsx(td, "c-tm whitespace-nowrap tabular-nums")}>
                      <span className="mb-0.5 inline-block rounded-md bg-surface-2 px-[7px] py-px text-xs font-semibold text-fg">
                        {order.rental_period} {t("giờ")}
                      </span>
                      <div className="text-[12.5px] text-muted">{rentalRange(order)}</div>
                    </td>

                    <td className={clsx(td, "c-cred")}>
                      {ended || booting ? (
                        <div className="revoked flex max-w-[200px] items-center gap-2 text-xs leading-snug text-faint">
                          <IcKeyRound className="h-[15px] w-[15px] flex-none" />
                          {ended
                            ? t("Máy đã kết thúc, thông tin đăng nhập đã thu hồi")
                            : t("Thông tin đăng nhập sẽ hiện khi máy tạo xong")}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-px font-mono text-[12.5px] text-fg">
                          {order.user_remote && (
                            <div className="cred-line flex items-center gap-1 whitespace-nowrap">
                              <span className={label}>TK</span>
                              <span className="mono">{order.user_remote}</span>
                              <CopyButton value={order.user_remote} label="Sao chép tài khoản" />
                            </div>
                          )}
                          {order.passRdp && (
                            <div
                              className={clsx(
                                "cred-line flex items-center gap-1 whitespace-nowrap rounded-md transition-colors duration-300",
                                pwNew === order._id && "bg-ok-soft font-semibold text-ok shadow-[0_0_0_3px_var(--ok-soft)]"
                              )}
                            >
                              <span className={label}>MK</span>
                              <span className="mono">{shown ? order.passRdp : "••••••••"}</span>
                              <button
                                type="button"
                                className={miniBtn}
                                title={shown ? t("Ẩn mật khẩu") : t("Hiện mật khẩu")}
                                onClick={() => toggleReveal(order._id)}
                              >
                                {shown ? <IcEyeOff className="h-[15px] w-[15px]" /> : <IcEye className="h-[15px] w-[15px]" />}
                              </button>
                              <CopyButton value={order.passRdp} label="Sao chép mật khẩu" />
                            </div>
                          )}
                          {order.remote_ext_ip && (
                            <div className="cred-line flex items-center gap-1 whitespace-nowrap">
                              <span className={label}>IP</span>
                              <span className="mono">{order.remote_ext_ip}</span>
                              <CopyButton value={order.remote_ext_ip} label="Sao chép địa chỉ remote" />
                            </div>
                          )}
                          {image.ssh_port && (
                            <div className="cred-line flex items-center gap-1 whitespace-nowrap">
                              <span className={label}>SSH</span>
                              <span className="mono">{image.ssh_port}</span>
                              <CopyButton value={String(image.ssh_port)} label="Sao chép SSH port" />
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    <td className={clsx(td, "c-st")}>
                      {running && <StatusPill tone="ok">{t("Đang chạy")}</StatusPill>}
                      {booting && <StatusPill tone="warn">{t("Đang khởi tạo")}</StatusPill>}
                      {ended && <StatusPill tone="muted">{t("Đã kết thúc")}</StatusPill>}
                    </td>

                    <td className={clsx(td, "c-act")} onClick={(e) => e.stopPropagation()}>
                      <div className={clsx("acts relative", !ended && "is-run", !ended && image.cloud_pc && on.onStart && "has-start")}>
                        {ended ? (
                          <>
                            <button type="button" className={clsx(btn, btnSoft, btnSm, "act-btn act-recreate")} onClick={() => on.onRecreate(order)}>
                              <IcRefresh className="h-4 w-4" />
                              {t("Tạo lại máy")}
                            </button>
                            <button
                              type="button"
                              disabled={!canDelete}
                              className={clsx(
                                iconBtn,
                                "act-icon del !h-8 !w-8 !rounded-lg",
                                canDelete ? "text-bad hover:!border-bad hover:bg-bad-soft" : "text-faint"
                              )}
                              title={
                                canDelete
                                  ? t("Xoá máy")
                                  : timeUp
                                  ? t("Máy đang được tắt, xoá được sau ít phút")
                                  : t("Máy đang được thuê ở đơn khác, không thể xoá")
                              }
                              onClick={() => !isBusy("delete", image._id) && on.onDelete(image._id)}
                            >
                              {isBusy("delete", image._id) ? <Spinner /> : <IcTrash className="h-4 w-4" />}
                            </button>
                          </>
                        ) : (
                          <>
                            {image.cloud_pc && on.onStart && (
                              <button
                                type="button"
                                className={clsx(btn, btnSoft, btnSm, "act-btn btn-start")}
                                disabled={!running || isBusy("start", image._id)}
                                title={t("Bật máy")}
                                onClick={() => on.onStart?.(image)}
                              >
                                {isBusy("start", image._id) ? <Spinner /> : <IcPower className="h-4 w-4" />}
                                <span className="lbl">{t("Bật máy")}</span>
                              </button>
                            )}
                            <button
                              type="button"
                              className={clsx(btn, btnPrimary, btnSm, "act-btn")}
                              disabled={!running || isBusy("rdp", image._id)}
                              onClick={() => on.onConnect(image)}
                            >
                              {isBusy("rdp", image._id) ? <Spinner /> : <IcMonitor className="h-4 w-4" />}
                              RDP
                            </button>
                            <button
                              type="button"
                              className={clsx(btn, btnPlay, btnSm, "act-btn btn-play")}
                              disabled={!running}
                              title="Play Game"
                              onClick={() => on.onPlay(image)}
                            >
                              <IcGamepad className="h-4 w-4" />
                              {/* luôn hiện chữ; bảng chật thì nút xếp 2 hàng (_rented.scss) */}
                              <span className="lbl">Play Game</span>
                            </button>
                            <button
                              type="button"
                              className={clsx(btn, btnGhost, btnSm, "act-btn btn-ext")}
                              disabled={!running || isBusy("extend", image._id)}
                              title={t("Gia hạn")}
                              onClick={() => on.onExtend(order)}
                            >
                              {isBusy("extend", image._id) ? <Spinner /> : <IcClock className="h-4 w-4" />}
                              <span className="lbl">{t("Gia hạn")}</span>
                            </button>
                            <button
                              type="button"
                              disabled
                              className={clsx(iconBtn, "act-icon del !h-8 !w-8 !rounded-lg text-faint")}
                              title={
                                booting
                                  ? t("Máy đang tạo, không thể xoá")
                                  : t("Máy đang chạy, không thể xoá. Chỉ xoá được máy đã kết thúc.")
                              }
                            >
                              <IcTrash className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={!running}
                              className={clsx(iconBtn, "act-icon !h-8 !w-8 !rounded-lg", menu?.order._id === order._id && "!border-brand !text-brand")}
                              // chặn mousedown để useOnClickOutside không đóng rồi click lại mở ngay
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => toggleMenu(order, e.currentTarget)}
                            >
                              {MENU_ACTIONS.some((action) => isBusy(action, image._id)) ? (
                                <Spinner className="text-brand" />
                              ) : (
                                <IcEllipsis className="h-4 w-4" />
                              )}
                            </button>
                          </>
                        )}

                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}

      {menu && (
        <div
          ref={menuRef}
          className="fixed z-[60] w-[200px] rounded-[10px] border border-line bg-surface p-1.5 text-left shadow-[0_10px_30px_rgba(0,0,0,.18)]"
          style={{ top: menu.top, bottom: menu.bottom, right: menu.right }}
        >
          {(
            [
              ["Đổi đường truyền", IcArrowLeftRight, "wan", () => on.onRouter(menu.order.image)],
              ["IP xoay", IcGlobe, "ip", () => on.onIpXoay(menu.order.image)],
              ["PCRender Tunnel", IcLink, "tunnel", () => on.onTunnel(menu.order.image)],
              ["Reset máy", IcRotateCw, "reset", () => on.onReset(menu.order.image)],
              // Cloud PC không có Đổi mật khẩu
              ...(on.onReConnect && !menu.order.image?.cloud_pc
                ? [["Đổi mật khẩu", IcKeyRound, "pw", () => on.onReConnect?.(menu.order.image._id)]]
                : []),
            ] as [string, typeof IcGlobe, string, () => void][]
          ).map(([text, Icon, key, action]) => {
            const running = isBusy(key, menu.order.image?._id);

            return (
              <button
                key={text}
                type="button"
                disabled={running}
                className="flex w-full items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left text-[13px] text-fg hover:bg-surface-2 disabled:cursor-default disabled:text-muted disabled:hover:bg-transparent"
                onClick={() => {
                  setMenu(null);
                  action();
                }}
              >
                {running ? <Spinner className="text-brand" /> : <Icon className="h-4 w-4 flex-none text-muted" />}
                {t(text)}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default RentedTable;
